import React, { useEffect, useMemo, useState } from 'react';
import {
  createAdminTestTheme,
  deleteAdminTestTheme,
  fetchAdminTestThemes,
  updateAdminTestTheme,
} from '../../utils/testApi';

const initialForm = {
  name: '',
  description: '',
  prompt_generate: '',
  prompt_grading: '',
};

function TestThemeManagement() {
  const [themes, setThemes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [search, setSearch] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(initialForm);

  const filteredThemes = useMemo(() => {
    const keyword = search.trim().toLowerCase();
    if (!keyword) return themes;
    return themes.filter((theme) => {
      const target = `${theme.name} ${theme.description}`.toLowerCase();
      return target.includes(keyword);
    });
  }, [themes, search]);

  const loadThemes = async () => {
    try {
      setLoading(true);
      setError('');
      const result = await fetchAdminTestThemes();
      setThemes(result.data?.themes || []);
    } catch (err) {
      setError(err.message || 'テーマ一覧の取得に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadThemes();
  }, []);

  const resetForm = () => {
    setEditingId(null);
    setForm(initialForm);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const validate = () => {
    if (!form.name.trim()) {
      setError('テーマ名を入力してください');
      return false;
    }
    if (!form.prompt_generate.trim()) {
      setError('AI問題生成プロンプトを入力してください');
      return false;
    }
    if (!form.prompt_grading.trim()) {
      setError('採点プロンプトを入力してください');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!validate()) {
      return;
    }

    try {
      setSaving(true);

      if (editingId) {
        await updateAdminTestTheme({
          id: editingId,
          ...form,
        });
        setSuccess('テーマを更新しました');
      } else {
        await createAdminTestTheme({
          ...form,
        });
        setSuccess('テーマを作成しました');
      }

      resetForm();
      await loadThemes();
    } catch (err) {
      setError(err.message || '保存に失敗しました');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (theme) => {
    setError('');
    setSuccess('');
    setEditingId(theme.id);
    setForm({
      name: theme.name || '',
      description: theme.description || '',
      prompt_generate: theme.prompt_generate || '',
      prompt_grading: theme.prompt_grading || '',
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDelete = async (id) => {
    if (!window.confirm('このテーマを削除しますか？')) {
      return;
    }

    setError('');
    setSuccess('');

    try {
      await deleteAdminTestTheme(id);
      setSuccess('テーマを削除しました');
      if (editingId === id) {
        resetForm();
      }
      await loadThemes();
    } catch (err) {
      setError(err.message || '削除に失敗しました');
    }
  };

  return (
    <div className="max-w-7xl mx-auto p-4 md:p-6">
      <div className="mb-6">
        <h1 className="text-2xl md:text-3xl font-bold text-gray-900">Webテストテーマ管理</h1>
        <p className="text-sm text-gray-600 mt-2">テーマ名・問題生成プロンプト・採点プロンプトを管理します。</p>
      </div>

      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-md">
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      {success && (
        <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-md">
          <p className="text-sm text-green-700">{success}</p>
        </div>
      )}

      <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow p-4 md:p-6 space-y-4 mb-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">テーマ名</label>
          <input
            type="text"
            name="name"
            value={form.name}
            onChange={handleChange}
            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
            placeholder="例: 接遇基礎"
            disabled={saving}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">説明</label>
          <textarea
            name="description"
            value={form.description}
            onChange={handleChange}
            rows={3}
            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
            placeholder="テーマ説明"
            disabled={saving}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">AI問題生成プロンプト</label>
          <textarea
            name="prompt_generate"
            value={form.prompt_generate}
            onChange={handleChange}
            rows={6}
            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
            placeholder="問題生成の指示を入力"
            disabled={saving}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">採点プロンプト</label>
          <textarea
            name="prompt_grading"
            value={form.prompt_grading}
            onChange={handleChange}
            rows={6}
            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
            placeholder="採点の指示を入力"
            disabled={saving}
          />
        </div>

        <div className="flex flex-wrap gap-2">
          <button
            type="submit"
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-60"
            disabled={saving}
          >
            {saving ? '保存中...' : editingId ? '更新する' : '作成する'}
          </button>

          {editingId && (
            <button
              type="button"
              className="px-4 py-2 bg-gray-100 text-gray-800 rounded-md hover:bg-gray-200"
              onClick={resetForm}
              disabled={saving}
            >
              編集をキャンセル
            </button>
          )}
        </div>
      </form>

      <div className="bg-white rounded-lg shadow p-4 md:p-6">
        <div className="flex items-center justify-between gap-2 mb-4">
          <h2 className="text-lg font-semibold text-gray-900">テーマ一覧</h2>
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full max-w-xs border border-gray-300 rounded-md px-3 py-2 text-sm"
            placeholder="テーマ検索"
          />
        </div>

        {loading ? (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600" />
          </div>
        ) : filteredThemes.length === 0 ? (
          <p className="text-sm text-gray-500">テーマがありません</p>
        ) : (
          <div className="space-y-3">
            {filteredThemes.map((theme) => (
              <div key={theme.id} className="border border-gray-200 rounded-md p-4">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div>
                    <p className="font-semibold text-gray-900">{theme.name}</p>
                    <p className="text-sm text-gray-600 mt-1 whitespace-pre-wrap">{theme.description || '説明なし'}</p>
                    <p className="text-xs text-gray-500 mt-2">作成日時: {new Date(theme.created_at).toLocaleString('ja-JP')}</p>
                  </div>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => handleEdit(theme)}
                      className="px-3 py-1.5 bg-gray-100 text-gray-800 text-sm rounded hover:bg-gray-200"
                    >
                      編集
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDelete(theme.id)}
                      className="px-3 py-1.5 bg-red-100 text-red-700 text-sm rounded hover:bg-red-200"
                    >
                      削除
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default TestThemeManagement;
